import { SelectOption } from '@common/types/common';
import { UserList } from '@common/types/user';
import { SuccessDialogContent } from '@components/display/dialogs';
import { Flag } from '@components/display/images';
import { BorderlessTableCell, Table } from '@components/display/tables';
import { CloseButton } from '@components/utility/buttons';
import { Filters } from '@components/utility/filters';
import { Select } from '@components/utility/selects';
import { Tabs } from '@components/utility/tabs';
import { useGetNewEventOwnerCandidatesQuery, usePatchEventOwnerMutation } from '@event/services';
import { EventTypes } from '@event/types';
import { useDebounce } from '@hooks/useDebounce';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutline';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { useGetCountriesQuery } from '@services/countries';
import { useGetRolesQuery } from '@services/userRoles';
import { capitalizeFirstLetter, handleError } from '@utils/common';
import { getRoleName, STANDARD_USER_TABLE_HEADERS } from '@utils/user';
import { useCallback, useImperativeHandle, useState } from 'react';
import { useParams } from 'react-router-dom';

const SEARCH_BY_OPTIONS: SelectOption<string>[] = [
    {
        value: 'name',
        label: 'Name',
    },
    {
        value: 'email',
        label: 'Email',
    },
    {
        value: 'company',
        label: 'Company',
    },
];

export type EventOwnerDialogRef = {
    open: () => void;
};

const EventOwnerDialog = ({ ref }: { ref: React.Ref<EventOwnerDialogRef> }) => {
    //#region State
    const [eventOwnerDialogOpen, setEventOwnerDialogOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [searchBy, setSearchBy] = useState(SEARCH_BY_OPTIONS[0].value);
    const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState<{
        column: keyof (UserList & { concatenated_name: string });
        direction: 'asc' | 'desc';
    }>({
        column: 'concatenated_name',
        direction: 'asc',
    });
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [newOwner, setNewOwner] = useState<UserList | null>(null);
    //#endregion

    //#region Hooks
    useImperativeHandle(ref, () => ({
        open: () => setEventOwnerDialogOpen(true),
        close: () => setEventOwnerDialogOpen(false),
    }));

    const debounceSearchInput = useDebounce({
        value: searchInput,
        delay: 500,
        onDebounce: () => {
            setPage(1);
            setNewOwner(null);
        },
        trigger: (prevValue) => searchInput !== prevValue,
    });

    const { type, eventId } = useParams();
    const eventType = type as EventTypes;
    //#endregion

    //#region Handlers
    const getChipLabel = (filter: string, item: string) => {
        const name = (() => {
            if (filter === 'country') return 'Country';
            else return capitalizeFirstLetter(filter);
        })();
        const value = (() => {
            if (filter === 'role') return getRoleName(item);
            else return capitalizeFirstLetter(item);
        })();
        return `${name}: ${value}`;
    };

    const handleChipClick = (filter: string, value: string) => {
        const newFilterState = activeFilters[filter]?.filter((x) => x !== value);
        if (newFilterState.length) {
            setActiveFilters((prevState) => ({
                ...prevState,
                [filter]: prevState[filter]?.filter((x) => x !== value),
            }));
        } else {
            setActiveFilters((prevState) => {
                const newState = { ...prevState };
                delete newState[filter];
                return newState;
            });
        }
        setNewOwner(null);
    };

    const onSelectChange = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value, name },
        } = event;
        setActiveFilters((prevState) => {
            const newState = { ...prevState };
            newState[name] = typeof value === 'string' ? value.split(',') : value;
            if (!newState[name].length) delete newState[name];
            return newState;
        });
        setPage(1);
        setNewOwner(null);
    };

    const renderRow = useCallback(
        (user: UserList) => (
            <>
                <BorderlessTableCell>
                    <div className="flex items-center gap-2">
                        {user.country && <Flag flag={user.country.toLowerCase()} className="w-[27px] h-[15px]" />}
                        <p>
                            {user.first_name && user.last_name
                                ? user.first_name + ' ' + user.last_name
                                : user.full_name}
                        </p>
                    </div>
                </BorderlessTableCell>
                <BorderlessTableCell>
                    <p>{user.company_name}</p>
                </BorderlessTableCell>
                <BorderlessTableCell>
                    <p>{user.role_display}</p>
                </BorderlessTableCell>
            </>
        ),
        []
    );

    const handleSortClick = (column: keyof (UserList & { concatenated_name: string })) => {
        const isAsc = sort.column === column && sort.direction === 'asc';
        setSort({
            column,
            direction: isAsc ? 'desc' : 'asc',
        });
    };

    const handleSelect = (user: UserList) => {
        setNewOwner((prevSelected) => (prevSelected?.id === user.id ? null : user));
    };

    const handleClose = () => {
        setEventOwnerDialogOpen(false);
        setTimeout(() => {
            setNewOwner(null);
            setPage(1);
            setSearchInput('');
            setSearchBy(SEARCH_BY_OPTIONS[0].value);
            setActiveFilters({});
            setSort({
                column: 'concatenated_name',
                direction: 'asc',
            });
            resetPatchEventOwner();
        }, 300);
    };
    //#endregion

    //#region Data Fetching
    const { data: roles } = useGetRolesQuery();
    const { data: countries } = useGetCountriesQuery();

    const orderingPrefix = sort.direction === 'desc' ? '-' : '';
    const ordering =
        sort.column === 'concatenated_name'
            ? [`${orderingPrefix}concatenated_name`]
            : `${orderingPrefix}${sort.column}`;

    const { data: newCandidates, isFetching: areNewCandidatesFetching } = useGetNewEventOwnerCandidatesQuery(
        {
            id: Number(eventId),
            type: eventType,
            page_size: rowsPerPage,
            page,
            ordering,
            filters: {
                ...activeFilters,
                [searchBy]: [debounceSearchInput],
            },
        },
        { skip: !eventOwnerDialogOpen }
    );
    //#endregion

    //#region Mutations
    const [
        patchEventOwner,
        { isLoading: isPatchingEventOwner, isSuccess: isPatchEventOwnerSuccess, reset: resetPatchEventOwner },
    ] = usePatchEventOwnerMutation();
    //#endregion

    const handleSave = async () => {
        try {
            if (!newOwner) throw new Error('No new owner selected');
            await patchEventOwner({ id: Number(eventId), type: eventType, owner_id: newOwner.id }).unwrap();
        } catch (err) {
            handleError('Error changing event owner', err);
        }
    };

    return (
        <Dialog open={eventOwnerDialogOpen} disableEscapeKeyDown classes={{ paper: 'dialog-paper py-10 w-[900px]' }}>
            {!isPatchEventOwnerSuccess ? (
                <>
                    <CloseButton onClick={handleClose} />
                    <DialogTitle className="pb-0">
                        <Typography fontFamily="museo-sans" fontSize={32} letterSpacing={-0.8} textAlign="center">
                            Change Event Owner
                        </Typography>
                        <Typography fontSize={16} textAlign="center">
                            Select a new event owner from the following list.
                        </Typography>
                        <Tabs className="mt-5" value={0}>
                            <Tabs.Tab
                                label={
                                    <Typography fontSize={14} fontWeight={500}>
                                        VicProf Users
                                    </Typography>
                                }
                                value={0}
                            />
                        </Tabs>
                    </DialogTitle>
                    <DialogContent>
                        <Filters
                            searchInput={searchInput}
                            onSearchChange={(e) => setSearchInput(e.target.value)}
                            onSearchClear={() => setSearchInput('')}
                            withFilterIcon={false}
                            dividers={false}
                            searchPlaceholder="Search"
                            searchProps={{
                                searchByState: [
                                    searchBy,
                                    (e) => {
                                        setSearchBy(e);
                                        setPage(1);
                                        setNewOwner(null);
                                    },
                                ],
                                searchByOptions: SEARCH_BY_OPTIONS,
                            }}
                            activeFilters={activeFilters}
                            activeFiltersPlacement="bottom"
                            getChipLabel={getChipLabel}
                            onChipClick={handleChipClick}
                            onClearFilterClick={() => {
                                setActiveFilters({});
                                setNewOwner(null);
                            }}
                            gridCols={3}
                            searchSpan={2}
                            className="!px-0"
                        >
                            <div className="grid grid-cols-3 gap-2.5">
                                <Select
                                    searchable
                                    multiple
                                    name="country"
                                    size="small"
                                    label="Country"
                                    items={countries ?? []}
                                    fcSX={{ size: 'small' }}
                                    renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                    onChange={(event) => onSelectChange(event as SelectChangeEvent<string[]>)}
                                    value={activeFilters['country'] ?? []}
                                    virtualize
                                    outlinedInputProps={{ className: 'shadow' }}
                                />
                                <Select
                                    searchable
                                    multiple
                                    name="role"
                                    size="small"
                                    label="Role"
                                    items={roles ?? []}
                                    fcSX={{ size: 'small' }}
                                    renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                    onChange={(event) => onSelectChange(event as SelectChangeEvent<string[]>)}
                                    value={activeFilters['role'] ?? []}
                                    outlinedInputProps={{ className: 'shadow' }}
                                />
                            </div>
                        </Filters>
                        <Table
                            selectable
                            alignSelectable
                            headers={STANDARD_USER_TABLE_HEADERS}
                            sort={sort}
                            data={newCandidates?.results ?? []}
                            querysetIds={newCandidates?.queryset_ids.map(Number) ?? []}
                            renderRow={renderRow}
                            emptyListTxt="There are currently no members to invite"
                            onSortClick={handleSortClick}
                            page={page}
                            onPageChange={(newPage) => setPage(newPage)}
                            selectedItems={newOwner ? [newOwner.id] : []}
                            onRowCheckClick={handleSelect}
                            count={newCandidates?.count ?? 0}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[
                                { label: '5', value: 5 },
                                { label: '10', value: 10 },
                                { label: '50', value: 50 },
                            ]}
                            setRowsPerPage={setRowsPerPage}
                            emptyListClassName="!min-h-[300px]"
                            isLoading={areNewCandidatesFetching}
                            scrollOnPageChange
                            counter={{ label: 'members selected' }}
                            separator="gray"
                            allSelectable={false}
                        />
                    </DialogContent>
                    <DialogActions className="flex w-full justify-center gap-5">
                        <Button
                            color="secondary-alt"
                            className="w-full rounded-full black-border"
                            onClick={() => {
                                setEventOwnerDialogOpen(false);
                                setNewOwner(null);
                            }}
                            startIcon={<ArrowBackIcon />}
                            size="small"
                        >
                            <span>Go Back</span>
                        </Button>
                        <Button
                            className="rounded-full w-full black-border"
                            disabled={!newOwner || isPatchingEventOwner}
                            onClick={handleSave}
                            startIcon={
                                isPatchingEventOwner ? <CircularProgress size={20} /> : <CheckCircleOutlinedIcon />
                            }
                            size="small"
                        >
                            <span>Confirm</span>
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <SuccessDialogContent
                        description={`Event Owner has been changed to ${
                            newOwner?.first_name && newOwner?.last_name
                                ? `${newOwner?.first_name} ${newOwner?.last_name}`
                                : newOwner?.full_name
                        } successfully!`}
                        btn={{
                            text: 'Continue',
                            onClick: handleClose,
                        }}
                    />
                </>
            )}
        </Dialog>
    );
};

export default EventOwnerDialog;
